"use client";

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FaLinkedinIn, FaFacebookF } from "react-icons/fa6";
import type { IconType } from "react-icons";
import { TeamMember } from "@/lib/types";
import AppButton from "../../shared/AppButton";
interface TeamInfoCardProps {
  member: TeamMember;
}
const SOCIAL_ICONS: Record<string, IconType> = {
  LinkedIn: FaLinkedinIn,
  Facebook: FaFacebookF
};

// Staggered per-line reveal. Incoming and outgoing blocks overlap (popLayout,
// below) so the swap reads as a buttery crossfade instead of a wait-then-snap —
// and the enter starts immediately, keeping it in step with the moving card.
const line: Variants = {
  hidden: {
    opacity: 0,
    y: 16,
    filter: "blur(6px)"
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.55,
      delay: i * 0.04,
      ease: [0.22, 1, 0.36, 1]
    }
  }),
  exit: (i: number) => ({
    opacity: 0,
    y: -14,
    filter: "blur(6px)",
    transition: {
      duration: 0.4,
      delay: i * 0.03,
      ease: [0.4, 0, 1, 1]
    }
  })
};

// Info panel synced to the currently active team member. Keyed on member.id so
// AnimatePresence transitions the whole block when the active card changes.
const TeamInfoCard = t0 => {
  const $ = _c(70);
  const {
    member
  } = t0;
  let T0;
  let T1;
  let T2;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== member.designation || $[1] !== member.id || $[2] !== member.name || $[3] !== member.socials) {
    const socials = member.socials.filter(_temp);
    t14 = "relative";
    T2 = AnimatePresence;
    t12 = "popLayout";
    t13 = false;
    T1 = motion.div;
    t5 = member.id;
    t6 = "hidden";
    t7 = "visible";
    t8 = "exit";
    t9 = "w-full space-y-4";
    if ($[21] !== member.designation) {
      t10 = <motion.p variants={line} custom={0} className="text-sm font-semibold uppercase tracking-[0.25em] text-primary">{member.designation}</motion.p>;
      $[21] = member.designation;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    if ($[23] !== member.name) {
      t11 = <motion.h3 variants={line} custom={1} className="font-black uppercase leading-[0.95] tracking-tighter text-3xl md:text-4xl xl:text-5xl">{member.name}</motion.h3>;
      $[23] = member.name;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    T0 = motion.div;
    t1 = line;
    t2 = 2;
    t3 = "flex gap-3 pt-1";
    t4 = socials.map(_temp2);
    $[0] = member.designation;
    $[1] = member.id;
    $[2] = member.name;
    $[3] = member.socials;
    $[4] = T0;
    $[5] = T1;
    $[6] = T2;
    $[7] = t1;
    $[8] = t10;
    $[9] = t11;
    $[10] = t12;
    $[11] = t13;
    $[12] = t14;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    T0 = $[4];
    T1 = $[5];
    T2 = $[6];
    t1 = $[7];
    t10 = $[8];
    t11 = $[9];
    t12 = $[10];
    t13 = $[11];
    t14 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
    t8 = $[19];
    t9 = $[20];
  }
  let t15;
  if ($[25] !== T0 || $[26] !== t1 || $[27] !== t2 || $[28] !== t3 || $[29] !== t4) {
    t15 = <T0 variants={t1} custom={t2} className={t3}>{t4}</T0>;
    $[25] = T0;
    $[26] = t1;
    $[27] = t2;
    $[28] = t3;
    $[29] = t4;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== member.bio) {
    t16 = <motion.p variants={line} custom={3} className="max-w-md text-base text-muted-foreground">{member.bio}</motion.p>;
    $[31] = member.bio;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Experience</p>;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== member.experience) {
    t18 = <div>{t17}<p className="font-bold">{member.experience}</p></div>;
    $[34] = member.experience;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Based in</p>;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== member.location) {
    t20 = <div>{t19}<p className="font-bold">{member.location}</p></div>;
    $[37] = member.location;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t18 || $[40] !== t20) {
    t21 = <motion.div variants={line} custom={4} className="flex gap-10 pt-1">{t18}{t20}</motion.div>;
    $[39] = t18;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== member.funFacts[0]) {
    t22 = <motion.p variants={line} custom={5} className="pt-1 text-sm italic text-muted-foreground">“{member.funFacts[0]}”</motion.p>;
    $[42] = member.funFacts[0];
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const t23 = `/about-us/${member.slug}`;
  let t24;
  let t25;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <span>View Profile</span>;
    t25 = <ArrowUpRight className="h-4 w-4" />;
    $[44] = t24;
    $[45] = t25;
  } else {
    t24 = $[44];
    t25 = $[45];
  }
  let t26;
  if ($[46] !== t23) {
    t26 = <motion.div variants={line} custom={6} className="flex pt-4"><AppButton href={t23} className="w-44" buttonClass="h-10">{t24}{t25}</AppButton></motion.div>;
    $[46] = t23;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== T1 || $[49] !== t10 || $[50] !== t11 || $[51] !== t15 || $[52] !== t16 || $[53] !== t21 || $[54] !== t22 || $[55] !== t26 || $[56] !== t5 || $[57] !== t6 || $[58] !== t7 || $[59] !== t8 || $[60] !== t9) {
    t27 = <T1 key={t5} initial={t6} animate={t7} exit={t8} className={t9}>{t10}{t11}{t15}{t16}{t21}{t22}{t26}</T1>;
    $[48] = T1;
    $[49] = t10;
    $[50] = t11;
    $[51] = t15;
    $[52] = t16;
    $[53] = t21;
    $[54] = t22;
    $[55] = t26;
    $[56] = t5;
    $[57] = t6;
    $[58] = t7;
    $[59] = t8;
    $[60] = t9;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== T2 || $[63] !== t12 || $[64] !== t13 || $[65] !== t27) {
    t28 = <T2 mode={t12} initial={t13}>{t27}</T2>;
    $[62] = T2;
    $[63] = t12;
    $[64] = t13;
    $[65] = t27;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== t14 || $[68] !== t28) {
    t29 = <div className={t14}>{t28}</div>;
    $[67] = t14;
    $[68] = t28;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  return t29;
};
export default TeamInfoCard;
function _temp(s) {
  return s.platform in SOCIAL_ICONS;
}
function _temp2(s_0) {
  const Icon = SOCIAL_ICONS[s_0.platform];
  return <a key={s_0.platform} href={s_0.href} target="_blank" rel="noopener noreferrer" aria-label={s_0.platform} className="grid h-11 w-11 place-items-center rounded-full bg-black text-white transition-all duration-300 hover:-translate-y-1 hover:bg-primary hover:text-black hover:shadow-[3px_3px_0_0_rgba(0,0,0,1)]"><Icon className="h-4.5 w-4.5" /></a>;
}